import { type NumericInput, parseCurrency } from '@/lib/utils'

/** The cart payment fields these helpers read (PosCartResource `payments`). Amounts are API money. */
export interface PartialRefundPayment {
  id?: number | null
  status?: string | null
  amount?: NumericInput
  source_payment_id?: number | null
  idempotency_key?: string | null
}

type MaybePartialRefundPayment = PartialRefundPayment | null | undefined

/** The POS cart fields these helpers read. */
export interface PartialRefundCart {
  payments?: MaybePartialRefundPayment[] | null
}

type MaybePartialRefundCart = PartialRefundCart | null | undefined

export function extractPendingCartRefundIdempotencyKey(
  cart: MaybePartialRefundCart
): string | null {
  const pendingRefundPayments = (cart?.payments ?? [])
    .filter((payment) => parseCurrency(payment?.amount) < 0)
    .filter((payment) => ['pending', 'processing'].includes(String(payment?.status ?? '')))
    .sort((left, right) => Number(right?.id ?? 0) - Number(left?.id ?? 0))

  for (const payment of pendingRefundPayments) {
    const sourcePaymentId = Number(payment?.source_payment_id ?? 0)
    const idempotencyKey = String(payment?.idempotency_key ?? '')
    const suffix = `:source-payment:${sourcePaymentId}`

    if (
      !sourcePaymentId ||
      !idempotencyKey.startsWith('cart-refund:') ||
      !idempotencyKey.endsWith(suffix)
    ) {
      continue
    }

    return idempotencyKey.slice('cart-refund:'.length, idempotencyKey.length - suffix.length)
  }

  return null
}

export function buildCartRefundPaymentSignature(cart: MaybePartialRefundCart): string {
  return JSON.stringify(
    (cart?.payments ?? [])
      .map((payment) => ({
        id: Number(payment?.id ?? 0),
        amount: parseCurrency(payment?.amount),
        status: String(payment?.status ?? ''),
        source_payment_id: Number(payment?.source_payment_id ?? 0),
        idempotency_key: String(payment?.idempotency_key ?? ''),
      }))
      .sort((left, right) => left.id - right.id)
  )
}
